// Import the functions you need from the SDKs you need
import AsyncLock from "async-lock";
import axios from "axios";
import { getAnalytics } from "firebase/analytics";
import { initializeApp } from "firebase/app";
import { getAuth, onAuthStateChanged, signInWithCustomToken, User } from "firebase/auth";
import { CatalogPlacement, CatalogScene, CatalogSort, CreditPack, CreditPackStatus, DetectedFace, DetectedOriginal, Face, Original, Streaming, Swap, TemporarySwap, UserData } from "./ApiTypes";

//===========================================================================
// Firebase初期化
//===========================================================================
const firebaseConfig = {
  apiKey: "AIzaSyDsN27oMh82vM7849tajQFOJ83YMm0I4PE",
  authDomain: "deepface-390117.firebaseapp.com",
  projectId: "deepface-390117",
  storageBucket: "deepface-390117.appspot.com",
  messagingSenderId: "671899292414",
  appId: "1:671899292414:web:5ea03180adc56613698baf",
  measurementId: "G-P8W5TVHFTZ"
};

export const app = initializeApp(firebaseConfig);
const analytics = getAnalytics(app);

//===========================================================================
// 全体の設定
//===========================================================================
interface GlobalConfig {
  DEBUG?: boolean;
  PORN_MODE?: boolean;
  BACKEND_ENDPOINT: string;
  TEST_CUSTOM_TOKEN?: string;
};

export const globalConfig = (window as any) as GlobalConfig;

export const getPornMode = () => {
  if (window.location.hostname == "video-swap.com") {
    return false;
  }
  if (globalConfig.PORN_MODE !== undefined) {
    return globalConfig.PORN_MODE
  }
  return true;
};

export const getSiteName = () => {
  return getPornMode() ? "PornSwap" : "VideoSwap";
};

//===========================================================================
// サーバー関連
//===========================================================================
export const getSeverEndpoint = () => (window as any).BACKEND_ENDPOINT;

export const getWsSeverEndpoint = () => getSeverEndpoint().replace("http", "ws").replace("https", "wss")

//===========================================================================
// ログイン関連
//===========================================================================

// 現在ログインしているユーザーを取得する
export const getLoginedUser = (): Promise<User|null> => {
  return new Promise((resolve) => {
    const currentUser = getAuth().currentUser;
    if (currentUser) {
      resolve(currentUser);
    } else {
      const unsubscribe = onAuthStateChanged(getAuth(), user => {
        resolve(user);
        unsubscribe();
      });
    };
  });
}

// ログインされるまで待機してユーザーを取得
const getUserWaitForLogin = (): Promise<User> => {
  return new Promise((resolve) => {
    const currentUser = getAuth().currentUser;
    if (currentUser) {
      resolve(currentUser);
    } else {
      const unsubscribe = onAuthStateChanged(getAuth(), user => {
        if (user) {
          resolve(user);
          unsubscribe();
        }
      });
    };
  });
}

const lock = new AsyncLock();

const createUser = async () => {
  await lock.acquire("createUser", async () => {
    await signInWithCustomToken(getAuth(), await createAccount());
    return;
  });
};

const requestBackend = async (user: User|null, name: string, data: FormData | { [key: string]: any }, noAuth?: boolean) => {
  try {
    let headers = {};
    if (user) {
      const idToken = await user.getIdToken();
      headers = { Authorization: idToken };
    }
    const response = await axios.post(`${getSeverEndpoint()}/${name}`, data, { headers });
    return response.data;
  }
  catch (exception) {
    console.log({ name, data, exception });
    throw exception;
  }
}

//===========================================================================
// エラー
//===========================================================================

// サーバーがソース動画を取得できないと判断したとき (`/create_swap` が 422)。
export class SourceVideoUnavailableError extends Error {}

// 対応していないサイトの URL を渡したとき (`/detect_video` が 422)。
export class UnsupportedSiteError extends Error {}

// 対応サイトだが動画ページではない URL を渡したとき（検索結果やトップページなど）。
export class NotAVideoPageError extends Error {}

// 元動画が削除・非公開になっているとき。同じ URL を試し続けても復活しない。
export class VideoNotFoundError extends Error {}

// 元動画が有料会員限定のとき。無料側には配信が無いので取り込めない。
export class PremiumOnlyVideoError extends Error {}

// 顔が写っていない画像を渡したとき (`/detect_face` が 422)。
export class FaceNotDetectedError extends Error {}

// カタログのシーンが無効化済み・削除済みだったとき (`/create_swap_from_scene` が 404)。
export class SceneNotFoundError extends Error {}

const detailOf = (exception: unknown) =>
  axios.isAxiosError(exception) ? exception.response?.data?.detail : undefined;

// サーバーが返す detail を、呼び出し側が出し分けできる例外に変換する。
const toApiError = (exception: unknown) => {
  switch (detailOf(exception)) {
    case "SOURCE_VIDEO_UNAVAILABLE": return new SourceVideoUnavailableError("SOURCE_VIDEO_UNAVAILABLE");
    case "UNSUPPORTED_SITE": return new UnsupportedSiteError("UNSUPPORTED_SITE");
    case "NOT_A_VIDEO_PAGE": return new NotAVideoPageError("NOT_A_VIDEO_PAGE");
    case "VIDEO_NOT_FOUND": return new VideoNotFoundError("VIDEO_NOT_FOUND");
    case "PREMIUM_ONLY_VIDEO": return new PremiumOnlyVideoError("PREMIUM_ONLY_VIDEO");
    case "FACE_NOT_DETECTED": return new FaceNotDetectedError("FACE_NOT_DETECTED");
    case "SCENE_NOT_FOUND": return new SceneNotFoundError("SCENE_NOT_FOUND");
    default: return exception;
  }
};

//===========================================================================
// POST API
//===========================================================================

export const checkIssue = async (swapId: string) => {
  const user = await getLoginedUser();
  const response = await requestBackend(user, "check_issue", {swapId});
  return response.haveIssue;
};

export const sendLoginCode = async (email: string) => {
  const user = await getLoginedUser();
  await requestBackend(user, "send_login_code", {email});
};

export const loginFromCode = async (email: string, loginCode: string) => {
  const user = await getLoginedUser();
  const response = await requestBackend(user, "login_from_code", {email, loginCode});
  return response.token as string;
};

export const signupFromEmail = async (email: string) => {
  const user = await getLoginedUser();
  const response = await requestBackend(user, "signup_from_email", {email});
  return response.token as string | null;
};

export const detectVideo = async (url: string) => {
  const user = await getLoginedUser();
  let response;
  try {
    response = await requestBackend(user, "detect_video", {url});
  } catch (exception) {
    throw toApiError(exception);
  }
  const result: DetectedOriginal = { 
    url,
    detected: {
      imageUrl: response.imageUrl as string, 
      title: response.title as string
    }
  };
  return result;
};

export const detectFace = async (file: Blob) => {
  const user = await getLoginedUser();
  const formData = new FormData();
  formData.append("file", file);
  let response;
  try {
    response = await requestBackend(user, "detect_face", formData);
  } catch (exception) {
    throw toApiError(exception);
  }
  const result: DetectedFace = {
    userId: user?.uid as string,
    faceId: response.faceId as string,
    croppedFace: response.croppedFace as string,
    meshedFace: response.meshedFace as string
  };
  return result;
};

export const processTemporarySwap = async (tmpSwapId: string) => {
  const user = await getLoginedUser();
  if (!user) {
    throw "Not loggedin";
  }
  await requestBackend(user, "process_temporary_swap", { tmpSwapId });
};

export const createSwap = async (originalUrl: string, originalThumbnailUrl: string, faceId: string, durationLimit?: number) => {
  const user = await getLoginedUser();
  if (!user) {
    throw "Not loggedin";
  }
  let response;
  try {
    response = await requestBackend(user, "create_swap", {
      originalUrl, 
      originalThumbnailUrl,
      faceId,
      successUrl: `${window.location.origin}/swaps/SWAP_ID?generating=1`,
      cancelUrl: `${window.location.origin}/swaps`,
      durationLimit
    });
  } catch (exception) {
    throw toApiError(exception);
  }
  return {
    swapId: response.swapId as string, 
    actionUrl: response.actionUrl as string | null
  };
};

export const createSwapNewUser = async (face: DetectedFace, original: DetectedOriginal, durationLimit?: number) => {
  let response;
  try {
    response = await requestBackend(null, "create_swap_new_user", {
      originalUrl: original.url, 
      originalThumbnailUrl: original.detected.imageUrl,
      originalTitle: original.detected.title,
      croppedFace: face.croppedFace as string,
      meshedFace: face.meshedFace as string,
      baseUrl: window.location.origin,
      durationLimit
    });
  } catch (exception) {
    throw toApiError(exception);
  }
  localStorage.setItem("temporarySwap", response.tmpSwapId);
  localStorage.removeItem("temporarySwapCanceled");
  return {
    tmpSwapId: response.tmpSwapId as string, 
    actionUrl: response.actionUrl as string
  };
}

export const checkoutSwap = async (swapId: string) => {
  const user = await getLoginedUser();
  if (!user) {
    throw "Not loggedin";
  }
  const response = await requestBackend(user, "checkout_swap", {
    swapId, 
    successUrl: `${window.location.origin}/swaps/SWAP_ID`,
    cancelUrl: `${window.location.origin}/swaps`
  });
  return { 
    actionUrl: response.actionUrl as string | null
  };
};

//===========================================================================
// シーンカタログ
//===========================================================================

// 選択中の顔に差し替えたサムネイルのカタログを取得する。
// 差し替えが終わっていないタイルはサーバー側で生成が始まるので、
// pendingCount が 0 になるまで呼び出し側がポーリングする。
export const getSceneCatalog = async (options: {
  faceId: string,
  placement: CatalogPlacement,
  sort?: CatalogSort,
  limit?: number,
  excludeSceneId?: string,
  reportView?: boolean
}) => {
  const user = await getLoginedUser();
  if (!user) {
    throw "Not loggedin";
  }
  const response = await requestBackend(user, "get_scene_catalog", {
    faceId: options.faceId,
    placement: options.placement,
    sort: options.sort ?? "used",
    limit: options.limit,
    excludeSceneId: options.excludeSceneId,
    reportView: options.reportView
  });
  return {
    scenes: response.scenes as CatalogScene[],
    pendingCount: response.pendingCount as number,
    hasPurchased: response.hasPurchased as boolean
  };
};

// カタログのタイルから生成を始める。保存済みカードがあれば actionUrl は null で、
// リダイレクトを挟まずそのまま生成が始まる（衝動を冷やさないための 1 タップ）。
export const createSwapFromScene = async (sceneId: string, faceId: string, placement: CatalogPlacement, durationLimit?: number) => {
  const user = await getLoginedUser();
  if (!user) {
    throw "Not loggedin";
  }
  let response;
  try {
    response = await requestBackend(user, "create_swap_from_scene", {
      sceneId,
      faceId,
      placement,
      successUrl: `${window.location.origin}/swaps/SWAP_ID?generating=1`,
      cancelUrl: `${window.location.origin}/swaps`,
      durationLimit
    });
  } catch (exception) {
    throw toApiError(exception);
  }
  return {
    swapId: response.swapId as string,
    actionUrl: response.actionUrl as string | null
  };
};

//===========================================================================
// クレジットパック
//===========================================================================

export const getCreditPacks = async () => {
  const user = await getLoginedUser();
  if (!user) {
    return [];
  }
  const response = await requestBackend(user, "get_credit_packs", {});
  return response.packs as CreditPack[];
};

export const checkoutCreditPack = async (packId: string) => {
  const user = await getLoginedUser();
  if (!user) {
    throw "Not loggedin";
  }
  const response = await requestBackend(user, "checkout_credit_pack", {
    packId,
    successUrl: `${window.location.origin}/credit_packs/PURCHASE_ID`,
    cancelUrl: `${window.location.origin}/scenes`
  });
  return {
    purchaseId: response.purchaseId as string,
    actionUrl: response.actionUrl as string | null
  };
};

// 決済からの復帰時に成立を確認する。ページ再読み込みで何度呼んでも
// クレジットは 1 回しか付与されず、2 回目以降は "already_granted" が返る。
export const confirmCreditPack = async (purchaseId: string, placement?: CatalogPlacement) => {
  const user = await getLoginedUser();
  if (!user) {
    throw "Not loggedin";
  }
  const response = await requestBackend(user, "confirm_credit_pack", { purchaseId, placement });
  return {
    status: response.status as CreditPackStatus,
    credits: response.credits as number,
    actionUrl: (response.actionUrl ?? null) as string | null
  };
};

export const createAccount = async () => {
  const response = await requestBackend(null, "create_account", {});
  return response.customToken as string;
};

export const deleteAccount = async () => {
  const user = await getLoginedUser();
  if (!user) {
    throw "Not loggedin";
  }
  await requestBackend(user, "delete_account", {});
};

export const getPaymentEmail = async (tmpSwapId: string) => {
  const response = await requestBackend(null, "get_payment_email", { tmpSwapId });
  return response.email as string | null;
};

export const getCustomToken = async () => {
  const user = await getLoginedUser();
  if (!user) {
    throw "Not loggedin";
  }
  const response = await requestBackend(user, "get_custom_token", {});
  return response.customToken as string;
};

export const sendEventPageView = async () => {
  const clarityCookie = document.cookie.replace(/(?:(?:^|.*;\s*)_clck\s*\=\s*([^;]*).*$)|^.*$/, "$1");
  const clarityUserId = decodeURIComponent(clarityCookie).split("|")[0];
  // await requestBackend(null, "page_view" + window.location.search + `&clarity=${clarityUserId}`, {});
};

//===========================================================================
// GET API
//===========================================================================

/**
 * スワップに素材と顔を結合する。
 *
 * サーバーは 3 つの一覧を別々に返す。結合してから返すと、同じ顔や素材が
 * スワップの数だけ重複するため、ここで組み立てる。
 */
const joinSwaps = (originals: Original[], faces: Face[], onlySwaps: any[]): Swap[] => {
  const facesById: { [key: string]: Face } = {};
  const originalsByUrl: { [key: string]: Original } = {};
  originals.forEach(original => {
    originalsByUrl[original.url] = original;
  });
  faces.forEach(face => {
    facesById[face.faceId] = face;
  });
  return onlySwaps.map(onlySwap => ({
    original: originalsByUrl[onlySwap.originalUrl],
    face: facesById[onlySwap.faceId],
    ...onlySwap
  }));
};

/**
 * 素材ページ（`/library`）・一覧・`/create` が必要とするものを 1 度で取る。
 *
 * 以前は Firestore を 3 回引いていた。画面単位で 1 往復にまとめている。
 */
export const getLibrary = async () => {
  const user = await getLoginedUser();
  if (!user) {
    return { originals: [], faces: [], swaps: [] };
  }
  const response = await requestBackend(user, "get_library", {});
  const originals = response.originals as Original[];
  const faces = response.faces as Face[];
  return { originals, faces, swaps: joinSwaps(originals, faces, response.swaps) };
};

export const getFaces = async (): Promise<Face[]> => (await getLibrary()).faces;

export const getOriginals = async (): Promise<Original[]> => (await getLibrary()).originals;

export const getSwaps = async (): Promise<Swap[]> => (await getLibrary()).swaps;

export const getSwap = async (swapId: string): Promise<Swap> => {
  const user = await getLoginedUser();
  if (!user) {
    throw "Not loggined"
  }
  const response = await requestBackend(user, "get_swap", { swapId });
  return { ...response.swap, original: response.original, face: response.face };
};

/**
 * ユーザー自身の情報。クレジット残高と、生成済みの動画を持っているか。
 *
 * 生成には課金かクレジットが必要なので、`hasGeneratedSwap` が true なら
 * 「一度は払って作ったことがある」と見なせる。
 */
export const getUserData = async (): Promise<UserData> => {
  const user = await getLoginedUser();
  if (!user) {
    throw "Not loggined"
  }
  return await requestBackend(user, "get_me", {}) as UserData;
};

export const hasGeneratedSwap = async (): Promise<boolean> => {
  const user = await getLoginedUser();
  if (!user) {
    return false;
  }
  const response = await requestBackend(user, "get_me", {});
  return response.hasGeneratedSwap === true;
};

/** 生成中に配信状態を追う間隔。生成完了で止めるので、数分だけ流れる。 */
const STREAMING_POLL_MS = 1500;

const decodeManifest = (manifest: { [key: string]: string }) => {
  const decoded: { [key: string]: Uint8Array } = {};
  Object.entries(manifest).forEach(([name, base64]) => {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    decoded[name] = bytes;
  });
  return decoded;
};

/**
 * 配信状態を購読する。以前は Firestore の `onSnapshot` だった。
 *
 * `manifest` は平均 21KB あるので毎回は取らず、配信が現れた時点で 1 回だけ取る。
 * 以降は状態だけをポーリングし、`generated` になったら止める。
 */
export const subscribeStreaming = async (streamingId: string, onChange: (streaming: Streaming) => void) => {
  const user = await getLoginedUser();
  if (!user) {
    throw "Not loggined"
  }
  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | undefined = undefined;
  let license: string | null = null;
  let manifest: { [key: string]: Uint8Array } | null = null;

  const tick = async () => {
    if (stopped) {
      return;
    }
    try {
      const state = await requestBackend(user, "get_streaming", { streamingId });
      if (state.exists) {
        if (manifest === null) {
          const full = await requestBackend(user, "get_streaming_manifest", { streamingId });
          license = full.license;
          manifest = decodeManifest(full.manifest);
        }
        if (!stopped) {
          onChange({ userId: user.uid, streamingId, status: state.status, license: license!, manifest });
        }
        if (state.status === "generated") {
          return;   // これ以上変化しない
        }
      }
    } catch (exception) {
      console.log({ subscribeStreaming: streamingId, exception });
    }
    if (!stopped) {
      timer = setTimeout(tick, STREAMING_POLL_MS);
    }
  };
  timer = setTimeout(tick, 0);

  return () => {
    stopped = true;
    if (timer !== undefined) {
      clearTimeout(timer);
    }
  };
};

export const getTemporarySwap = async (tmpSwapId: string): Promise<TemporarySwap|null> => {
  try {
    return await requestBackend(null, "get_temporary_swap", { tmpSwapId }) as TemporarySwap;
  } catch (exception) {
    return null;
  }
};
